/**
 * Générateur — « Utiliser les notions de multiples et diviseurs. »
 * Attendu ID : 907 | Niveau : 5ème | Domaine : Nombres et calculs / Opérations
 *
 * Mis en conformité avec la spécification 907.json (campagne 5e, 2026-09-23).
 * La spécification est la seule référence ; ce qu'elle ne dit pas est noté
 * dans le rapport audit/5eme/907.md (section « Non tranché »).
 *
 * ─── Plan de cartes ────────────────────────────────────────────────────────
 *
 *   Carte « vocabulaire » — Multiple, diviseur ou divisible ? (niveau 1)
 *     Formats : texte_trou_drag (étiquettes multiple / diviseur / divisible),
 *     qcm_unique (la même phrase, liste fermée des trois mots).
 *     Quatre gabarits sur un produit de deux facteurs distincts, tous deux
 *     compris entre 2 et 12, produit au plus égal à 100 ; le quatrième pose
 *     la chaîne a | m | M par construction, avec M au plus égal à 100.
 *
 *   Carte « jugement » — Diviseur, multiple, ou ni l'un ni l'autre ? (niveau 1)
 *     Format : qcm_unique. C'est le format « jugement » que prescrit
 *     `type_reponse` : liste fermée de trois propositions (« … est un diviseur
 *     de … », « … est un multiple de … », « ni l'un ni l'autre »), dont le
 *     statut est CALCULÉ sur les deux nombres et jamais rattaché à un libellé.
 *     Les trois cas sont tirés à parts égales ; le cofacteur sort parfois des
 *     tables mémorisées.
 *
 *   Carte « vrai_faux » — Vrai ou faux ? (niveau 1)
 *     Formats : tableau_vf (3 formulations croisées d'un couple + 2 énoncés
 *     conceptuels de codes différents), qcm_multiple (3 vraies, 3 fausses,
 *     compte annoncé). Trois familles conceptuelles, chacune dans ses deux
 *     polarités : 1 comme diviseur universel, le premier multiple non nul
 *     d'un entier, un multiple hors des tables mémorisées.
 *
 *   Carte « multiples » — Les premiers multiples (niveau 2)
 *     Formats : texte_trou_saisie (les 3 à 6 premiers multiples NON NULS,
 *     dans l'ordre croissant, une case chacun ; le nombre attendu est écrit
 *     dans la consigne), qcm_unique (le multiple de rang donné).
 *
 *   Carte « division » — Division euclidienne et diviseurs (niveau 4)
 *     Formats : texte_trou_saisie (a = b × q + r, reste éventuellement nul),
 *     qcm_unique (verdict AVEC justification : c'est le reste nul, pas le
 *     quotient entier, qui décide), qcm_unique de situation (quotient, reste
 *     ou quotient augmenté de 1, selon la question posée sur les mêmes
 *     nombres). Diviseur de 2 à 12, dividende de 10 à 100.
 *
 *   Carte « communs » — Diviseurs communs et bouquets (niveaux 3 et 7)
 *     Formats : texte_trou_saisie (TOUS les diviseurs d'un entier de 10 à 100,
 *     dans l'ordre croissant, 1 et le nombre lui-même compris — la consigne le
 *     dit), qcm_multiple (les diviseurs communs de deux entiers, compte
 *     annoncé), texte (partage exact : le plus grand diviseur commun).
 *
 *   Carte « intervalle » — Un multiple ou un diviseur dans un intervalle (niveau 5)
 *     Format : texte (saisie). Rotation multiple / diviseur. L'intervalle est
 *     construit pour contenir EXACTEMENT une solution, annoncée par la
 *     consigne ; le multiple sort parfois des tables mémorisées.
 *
 *   Carte « fraction » — Simplifier une fraction (niveau 6)
 *     Formats : texte_trou_saisie (numérateur et dénominateur de la fraction
 *     irréductible, trous KaTeX), qcm_unique (le diviseur commun par lequel
 *     simplifier en une fois). Numérateur et dénominateur jamais premiers
 *     entre eux, dénominateur de 4 à 100.
 *
 *   Carte « periodique » — Deux phénomènes qui reviennent (niveau 7)
 *     Formats : qcm_unique, texte (saisie). Conjonction de deux phénomènes
 *     périodiques : le plus petit multiple commun des deux périodes.
 *
 * ─── Spécification (907.json) ──────────────────────────────────────────────
 *
 *   Bornes appliquées :
 *     - facteurs d'une factorisation : 2 à 12, deux facteurs, distincts,
 *       produit au plus égal à 100 ;
 *     - entier dont on cherche les diviseurs : 10 à 100 ;
 *     - entier dont on cherche les multiples : 2 à 100, tous les multiples
 *       produits restant eux-mêmes au plus égaux à 100 ;
 *     - nombre de multiples demandés : 3 à 6, toujours écrit dans la consigne ;
 *     - division euclidienne : dividende 10 à 100, diviseur 2 à 12, reste de
 *       0 à diviseur − 1 (reste non nul une fois sur deux, et toujours dans
 *       la carte de situation) ;
 *     - nombre de solutions attendues : 1, annoncé par la consigne ;
 *     - dénominateur de la fraction à simplifier : 4 à 100.
 *   Exclusions appliquées : aucun entier au delà de 100 ; aucun multiple nul
 *     (les consignes disent « non nuls ») ; aucun facteur égal à 1 ni au
 *     nombre lui-même dans les factorisations ; deux facteurs toujours
 *     distincts (donc aucun carré parfait) ; aucun intervalle dont le nombre
 *     de solutions diffère de celui annoncé (construction, pas vérification) ;
 *     aucune fraction dont le numérateur et le dénominateur sont premiers
 *     entre eux ; aucune divisibilité présentée comme relevant d'un critère.
 *   Erreurs visées → mots-clés (codes de `_meta.params.distracteurs[].code`
 *     ou `_meta.params.erreurVisee`) :
 *     1. « Inverser les rôles… »                              → roles_inverses
 *     2. « Conclure qu'un grand nombre n'est pas multiple… »   → hors_table_donc_non_multiple
 *     3. « Oublier que 1 et le nombre lui-même… »              → un_et_lui_meme
 *     4. « Oublier qu'un entier est son propre premier multiple » → premier_multiple_est_lui_meme
 *     5. « Traiter un problème de répartition exacte… »        → repartition_ou_reste
 *     6. « Donner le quotient sans interpréter le reste »      → reste_non_interprete
 *     7. « Additionner les deux périodes… »                    → periodes_additionnees
 *     Trois distracteurs imposés par le format ne matérialisent aucune de ces
 *     erreurs et portent un code propre, annoncé ici : `vocabulaire_confondu`
 *     (le troisième mot de la liste fermée), `simplification_incomplete` (un
 *     diviseur commun trop petit), `produit_au_lieu_du_ppcm` et
 *     `diviseur_au_lieu_de_multiple` (les deux confusions de la carte
 *     périodique).
 *   Graduation → cartes :
 *     niveau 1 → vocabulaire (2 formats), jugement, vrai_faux (2 formats) ;
 *     niveau 2 → multiples (2 formats) ;
 *     niveau 3 → communs_texte_trou_saisie, communs_qcm_multiple ;
 *     niveau 4 → division (3 formats) ;
 *     niveau 5 → intervalle_texte ;
 *     niveau 6 → fraction (2 formats) ;
 *     niveau 7 → communs_texte (partage exact), periodique (2 formats).
 *   Non tranché (détail dans le rapport) :
 *     - le format « liste séparée par des points-virgules » du `type_reponse`
 *       n'existe pas dans l'application ; la liste passe par un texte à trous,
 *       une case par valeur, l'ordre croissant étant imposé par la consigne ;
 *     - `variables.reste_de_la_division_euclidienne.min = 1` contredit la
 *       justification du même champ (« le reste doit être non nul dans au
 *       moins une question sur cinq ») et le niveau 1 de la graduation, qui
 *       demande de reconnaître une divisibilité : le reste nul est conservé,
 *       le reste non nul est tiré dans [1 ; diviseur − 1] ;
 *     - l'erreur visée 2 (« un grand nombre qui n'apparaît pas dans la table »)
 *       exige un cofacteur supérieur à 12, ce que la borne
 *       `valeur_de_chaque_facteur` (2 à 12) interdirait si on l'appliquait
 *       hors des factorisations : les trois cartes concernées ne posent
 *       jamais la tâche de factoriser.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs, fracTex, fracTrouTex,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 907;

const L_VOC = 'Multiple, diviseur ou divisible ?';
const L_JUG = 'Diviseur, multiple, ou ni l\'un ni l\'autre ?';
const L_VF  = 'Vrai ou faux ? (multiples et diviseurs)';
const L_MUL = 'Les premiers multiples';
const L_DIV = 'Division euclidienne et diviseurs';
const L_COM = 'Diviseurs communs et bouquets';
const L_INT = 'Un multiple ou un diviseur dans un intervalle';
const L_FRA = 'Simplifier une fraction';
const L_PER = 'Deux phénomènes qui reviennent';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Paramètres de tirage exposés au harnais et au test de conformité (jamais persistés). */
const avecParams = (payload, params) => {
    payload._meta.params = params;
    return payload;
};

const MOTS_VOC = ['multiple', 'diviseur', 'divisible'];
const MOTS_COMPTE = { 2: 'deux', 3: 'trois', 4: 'quatre', 5: 'cinq', 6: 'six' };
const ORDINAUX = { 3: 'troisième', 4: 'quatrième', 5: 'cinquième', 6: 'sixième' };

const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));
const ppcm = (a, b) => (a * b) / pgcd(a, b);

/** Diviseurs d'un entier, dans l'ordre croissant (calculés, pas de pool). */
const diviseursDe = (n) => {
    const res = [];
    for (let d = 1; d <= n; d++) if (n % d === 0) res.push(d);
    return res;
};

/**
 * Deux facteurs DISTINCTS de 2 à 12 et leur produit, au plus égal à 100.
 * Spéc. 907 : `valeur_de_chaque_facteur` 2 à 12, deux facteurs, aucun égal
 * à 1 ; exclusion « aucun carré parfait » → les deux facteurs diffèrent.
 */
const tirerFacteurs = () => {
    for (let i = 0; i < 60; i++) {
        const a = randInt(2, 12);
        const k = randInt(2, 12);
        if (a === k || a * k > 100) continue;
        return { a, k, n: a * k };
    }
    return { a: 3, k: 7, n: 21 };
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Le bon vocabulaire (niveau 1)
// ═══════════════════════════════════════════════════════════

/** 4 gabarits — phrase à trous + version QCM (phrase, mot attendu, piège). */
const tirerVocabulaire = (simpleSeulement = false) => {
    const variante = simpleSeulement ? randInt(0, 2) : randInt(0, 3);
    if (variante < 3) {
        const { a, n } = tirerFacteurs();
        if (variante === 0) {
            return { phrase: `${n} est un {{multiple}} de ${a}.`, enonce: `${n} est un … de ${a}.`, mot: 'multiple', piege: 'diviseur' };
        }
        if (variante === 1) {
            return { phrase: `${a} est un {{diviseur}} de ${n}.`, enonce: `${a} est un … de ${n}.`, mot: 'diviseur', piege: 'multiple' };
        }
        return { phrase: `${n} est {{divisible}} par ${a}.`, enonce: `${n} est … par ${a}.`, mot: 'divisible', piege: 'diviseur' };
    }
    // Chaîne a | m | M posée par construction, M au plus égal à 100.
    const a = randInt(2, 6);
    const m = a * randInt(2, 5);
    const grand = m * randInt(2, Math.floor(100 / m));
    return {
        phrase: `${m} est un {{multiple}} de ${a} et un {{diviseur}} de ${grand}.`,
        enonce: null, mot: null, piege: 'diviseur',
    };
};

/** Code d'un mot proposé : le mot qui inverse les rôles, ou le troisième mot. */
const codeMotVoc = (mot, piege) => (mot === piege ? 'roles_inverses' : 'vocabulaire_confondu');

const genVocabulaireDrag = () => {
    const t = tirerVocabulaire();
    const distracteurs = filtrerDistracteurs(t.phrase, MOTS_VOC);
    const texte = '<p>Complète avec le bon vocabulaire :</p>';
    const payload = makeTexteTrou(t.phrase, texte, { ...opts(L_VOC), mode: 'drag', distracteurs });
    return avecParams(payload, {
        niveau: 1,
        distracteurs: distracteurs.map(d => ({ valeur: d, code: codeMotVoc(d, t.piege) })),
    });
};

const genVocabulaireQcm = () => {
    const t = tirerVocabulaire(true);
    const texte = `<p>Complète la phrase : <strong>« ${t.enonce} »</strong></p>`;
    const payload = makeQcm([...MOTS_VOC], t.mot, texte, opts(L_VOC));
    return avecParams(payload, {
        niveau: 1,
        reponse: t.mot,
        distracteurs: MOTS_VOC.filter(m => m !== t.mot).map(m => ({ valeur: m, code: codeMotVoc(m, t.piege) })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Diviseur, multiple, ou ni l'un ni l'autre ? (niveau 1)
// Format « jugement » prescrit par `type_reponse` : liste fermée de trois
// propositions, la troisième étant « ni l'un ni l'autre ».
// ═══════════════════════════════════════════════════════════

const tirerJugement = () => {
    const relation = randomChoice(['diviseur', 'multiple', 'aucun']);
    if (relation === 'aucun') {
        for (let i = 0; i < 60; i++) {
            const x = randInt(3, 99);
            const y = randInt(3, 99);
            if (x === y || x % y === 0 || y % x === 0) continue;
            return { x, y, horsTable: false };
        }
        return { x: 15, y: 28, horsTable: false };
    }
    for (let i = 0; i < 60; i++) {
        const petit = randInt(2, 12);
        const k = randInt(2, Math.floor(100 / petit));
        if (k === petit) continue;                       // deux facteurs distincts
        const grand = petit * k;
        return relation === 'diviseur'
            ? { x: petit, y: grand, horsTable: k > 12 }
            : { x: grand, y: petit, horsTable: k > 12 };
    }
    return relation === 'diviseur' ? { x: 7, y: 91, horsTable: true } : { x: 91, y: 7, horsTable: true };
};

const genJugementQcm = () => {
    const t = tirerJugement();
    const estDiviseur = `${t.x} est un diviseur de ${t.y}`;
    const estMultiple = `${t.x} est un multiple de ${t.y}`;
    const NI = 'Ni l\'un ni l\'autre';
    // Statut CALCULÉ sur les nombres, jamais rattaché à un libellé.
    let bonne = NI;
    if (t.x !== t.y && t.y % t.x === 0) bonne = estDiviseur;
    else if (t.x !== t.y && t.x % t.y === 0) bonne = estMultiple;
    const texte = `<p>Que peut-on dire de <strong>${t.x}</strong> et de <strong>${t.y}</strong> ? ` +
        'Choisis la phrase vraie.</p>';
    const payload = makeQcm([estDiviseur, estMultiple, NI], bonne, texte, opts(L_JUG));
    return avecParams(payload, {
        niveau: 1, premier: t.x, second: t.y, reponse: bonne,
        distracteurs: [estDiviseur, estMultiple, NI]
            .filter(c => c !== bonne)
            .map(c => ({ valeur: c, code: c === NI ? 'hors_table_donc_non_multiple' : 'roles_inverses' })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Vrai ou faux ? (niveau 1)
// ═══════════════════════════════════════════════════════════

/**
 * Les 6 formulations croisées d'un couple (a, n = a·k) et trois familles
 * conceptuelles, chacune dans SES DEUX polarités (aucune formulation n'est
 * vraie dans 100 % ni dans 0 % des tirages).
 */
const tirerPhrasesVF = () => {
    const { a, n } = tirerFacteurs();
    const couple = [
        { txt: `${n} est un multiple de ${a}`, v: true,  code: 'roles_inverses' },
        { txt: `${n} est un diviseur de ${a}`, v: false, code: 'roles_inverses' },
        { txt: `${n} est divisible par ${a}`,  v: true,  code: 'roles_inverses' },
        { txt: `${a} est un multiple de ${n}`, v: false, code: 'roles_inverses' },
        { txt: `${a} est un diviseur de ${n}`, v: true,  code: 'roles_inverses' },
        { txt: `${a} est divisible par ${n}`,  v: false, code: 'roles_inverses' },
    ];
    // Hors des tables mémorisées : cofacteur au moins égal à 13, produit < 100.
    const p = randInt(3, 7);
    const grand = p * randInt(13, Math.floor(99 / p));
    const autreQue1 = randInt(2, 9);
    const conceptuel = [
        { txt: '1 est un diviseur de tous les nombres entiers', v: true,  code: 'un_et_lui_meme' },
        { txt: `${autreQue1} est un diviseur de tous les nombres entiers`, v: false, code: 'un_et_lui_meme' },
        { txt: `${n} est un diviseur de ${n}`,     v: true,  code: 'un_et_lui_meme' },
        { txt: `${a} est un diviseur de ${n + 1}`, v: false, code: 'un_et_lui_meme' },
        { txt: `Le premier multiple non nul de ${a} est ${a}`,     v: true,  code: 'premier_multiple_est_lui_meme' },
        { txt: `Le premier multiple non nul de ${a} est ${2 * a}`, v: false, code: 'premier_multiple_est_lui_meme' },
        { txt: `${grand} est un multiple de ${p}`,     v: true,  code: 'hors_table_donc_non_multiple' },
        { txt: `${grand + 1} est un multiple de ${p}`, v: false, code: 'hors_table_donc_non_multiple' },
    ];
    return { couple, conceptuel };
};

const CODES_CONCEPTUELS = ['un_et_lui_meme', 'premier_multiple_est_lui_meme', 'hors_table_donc_non_multiple'];

const genVFTableau = () => {
    const { couple, conceptuel } = tirerPhrasesVF();
    const codes = shuffle(CODES_CONCEPTUELS).slice(0, 2);
    let lignes = [];
    for (let i = 0; i < 30; i++) {
        lignes = [
            ...shuffle(couple).slice(0, 3),
            ...codes.map(c => randomChoice(conceptuel.filter(x => x.code === c))),
        ];
        const nbVrais = lignes.filter(l => l.v).length;
        if (nbVrais >= 1 && nbVrais < lignes.length) break;
    }
    lignes = shuffle(lignes);
    const texte = '<p>Pour chaque phrase, indique si elle est vraie ou fausse.</p>';
    const payload = makeVraiFaux(
        lignes.map(l => l.txt), lignes.map(l => (l.v ? 0 : 1)), texte, opts(L_VF),
    );
    return avecParams(payload, {
        niveau: 1,
        distracteurs: lignes.filter(l => !l.v).map(l => ({ valeur: l.txt, code: l.code })),
    });
};

const genVFQcmMulti = () => {
    const { couple, conceptuel } = tirerPhrasesVF();
    const vraies  = shuffle([...couple, ...conceptuel].filter(p => p.v)).slice(0, 3);
    const fausses = shuffle([...couple, ...conceptuel].filter(p => !p.v)).slice(0, 3);
    const pool = shuffle([...vraies, ...fausses]);
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[vraies.length]}</strong> phrases <strong>vraies</strong>.</p>`;
    const payload = makeQcm(pool.map(p => p.txt), vraies.map(p => p.txt), texte, opts(L_VF), true);
    return avecParams(payload, {
        niveau: 1,
        distracteurs: fausses.map(p => ({ valeur: p.txt, code: p.code })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Les premiers multiples (niveau 2)
// ═══════════════════════════════════════════════════════════

const genMultiplesTrous = () => {
    const nb = randInt(3, 6);
    const a = randInt(2, Math.floor(100 / nb));
    const cases = [];
    for (let i = 1; i <= nb; i++) cases.push(`{{${i * a}}}`);
    const phrase = `Les ${MOTS_COMPTE[nb]} premiers multiples non nuls de ${a}, dans l'ordre croissant : ${cases.join(' ; ')}`;
    const texte = `<p>Écris les <strong>${MOTS_COMPTE[nb]}</strong> premiers multiples ` +
        `<strong>non nuls</strong> de <strong>${a}</strong>, dans l'ordre croissant, ` +
        'une case pour chacun.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_MUL), mode: 'saisie' });
    return avecParams(payload, {
        niveau: 2, nombre: a, nbMultiples: nb,
        erreurVisee: 'premier_multiple_est_lui_meme',
    });
};

/** Entiers composés : le plus grand diviseur propre est un distracteur crédible. */
const COMPOSES_MULTIPLE = [4, 6, 8, 9, 10, 12, 14, 15, 16];

const genMultiplesQcm = () => {
    let rang = 3;
    let a = 6;
    for (let i = 0; i < 60; i++) {
        const r = randInt(3, 6);
        const x = randomChoice(COMPOSES_MULTIPLE);
        if ((r + 1) * x <= 100) { rang = r; a = x; break; }
    }
    const bonne = rang * a;
    const diviseurPropre = diviseursDe(a).filter(d => d < a).pop();
    const choix = [bonne, (rang + 1) * a, (rang - 1) * a, diviseurPropre].map(String);
    const texte = `<p>Quel est le <strong>${ORDINAUX[rang]}</strong> multiple ` +
        `<strong>non nul</strong> de <strong>${a}</strong> ?</p>`;
    const payload = makeQcm(choix, String(bonne), texte, opts(L_MUL));
    return avecParams(payload, {
        niveau: 2, nombre: a, rang, reponse: bonne,
        distracteurs: [
            { valeur: String((rang + 1) * a), code: 'premier_multiple_est_lui_meme' },
            { valeur: String((rang - 1) * a), code: 'premier_multiple_est_lui_meme' },
            { valeur: String(diviseurPropre), code: 'roles_inverses' },
        ],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Division euclidienne et diviseurs (niveau 4)
// ═══════════════════════════════════════════════════════════

/** Dividende 10 à 100, diviseur 2 à 12, reste 0 ou dans [1 ; diviseur − 1]. */
const tirerDivision = (resteNonNul = false) => {
    for (let i = 0; i < 60; i++) {
        const b = randInt(2, 12);
        const divisible = resteNonNul ? false : randomChoice([true, false]);
        const r = divisible ? 0 : randInt(1, b - 1);
        const qMin = Math.max(2, Math.ceil((10 - r) / b));
        const qMax = Math.floor((100 - r) / b);
        if (qMax < qMin) continue;
        const q = randInt(qMin, qMax);
        return { a: b * q + r, b, q, r, divisible };
    }
    return { a: 47, b: 6, q: 7, r: 5, divisible: false };
};

const genDivisionTrous = () => {
    const t = tirerDivision();
    const phrase = `${t.a} = ${t.b} × {{${t.q}}} + {{${t.r}}}`;
    const texte = `<p>Effectue la division euclidienne de <strong>${t.a}</strong> par ` +
        `<strong>${t.b}</strong>. Complète : le premier nombre est le quotient, ` +
        'le second est le reste, qui peut être nul.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_DIV), mode: 'saisie' });
    return avecParams(payload, {
        niveau: 4, dividende: t.a, diviseur: t.b, quotient: t.q, reste: t.r,
        erreurVisee: 'reste_non_interprete',
    });
};

const genDivisionQcm = () => {
    const t = tirerDivision();
    const bonne = t.divisible
        ? 'Oui, car le reste de la division euclidienne est nul'
        : `Non, car le reste de la division euclidienne vaut ${t.r}`;
    const inverse = t.divisible
        ? 'Non, car le reste de la division euclidienne n\'est pas nul'
        : 'Oui, car le reste de la division euclidienne est nul';
    const quotientEntier = `Oui, car le quotient (${t.q}) est un nombre entier`;
    const rolesInverses = `Oui, car ${t.a} est un diviseur de ${t.b}`;
    const texte = `<p>On donne : <strong>${t.a} = ${t.b} × ${t.q} + ${t.r}</strong>.</p>` +
        `<p><strong>${t.b}</strong> est-il un diviseur de <strong>${t.a}</strong> ? ` +
        'Choisis la réponse accompagnée de la justification correcte.</p>';
    const payload = makeQcm([bonne, inverse, quotientEntier, rolesInverses], bonne, texte, opts(L_DIV));
    return avecParams(payload, {
        niveau: 4, dividende: t.a, diviseur: t.b, reste: t.r, divisible: t.divisible,
        distracteurs: [
            { valeur: inverse, code: 'reste_non_interprete' },
            { valeur: quotientEntier, code: 'reste_non_interprete' },
            { valeur: rolesInverses, code: 'roles_inverses' },
        ],
    });
};

// Les groupes nominaux sont ACCORDÉS dans la table (jamais un article recollé
// devant un nom variable) : « toutes les photos », « une page complète ».
const CTX_DIVISION = [
    {
        objet: 'livres', objetTous: 'tous les livres', ranges: 'rangés',
        contenant: 'cartons', chaque: 'chaque carton', unComplet: 'un carton complet', complets: 'complets',
    },
    {
        objet: 'billes', objetTous: 'toutes les billes', ranges: 'rangées',
        contenant: 'sachets', chaque: 'chaque sachet', unComplet: 'un sachet complet', complets: 'complets',
    },
    {
        objet: 'photos', objetTous: 'toutes les photos', ranges: 'rangées',
        contenant: 'pages', chaque: 'chaque page', unComplet: 'une page complète', complets: 'complètes',
    },
];

const QUESTIONS_DIVISION = ['reste', 'complets', 'necessaires'];

const genDivisionSituation = () => {
    let t = tirerDivision(true);
    for (let i = 0; i < 40 && (t.r === t.q || t.r === t.q + 1); i++) t = tirerDivision(true);
    if (t.r === t.q || t.r === t.q + 1) t = { a: 47, b: 6, q: 7, r: 5, divisible: false };
    const ctx = randomChoice(CTX_DIVISION);
    const question = randomChoice(QUESTIONS_DIVISION);
    const demande = {
        reste: `Combien de ${ctx.objet} ne pourront pas être ${ctx.ranges} dans ${ctx.unComplet} ?`,
        complets: `Combien de ${ctx.contenant} seront ${ctx.complets} ?`,
        necessaires: `Combien de ${ctx.contenant} faut-il en tout pour ranger ${ctx.objetTous} ?`,
    }[question];
    const bonne = { reste: t.r, complets: t.q, necessaires: t.q + 1 }[question];
    const codes = {
        reste: { [t.q]: 'reste_non_interprete', [t.q + 1]: 'repartition_ou_reste' },
        complets: { [t.r]: 'reste_non_interprete', [t.q + 1]: 'repartition_ou_reste' },
        necessaires: { [t.r]: 'reste_non_interprete', [t.q]: 'repartition_ou_reste' },
    }[question];
    const choix = [t.q, t.q + 1, t.r].map(String);
    const texte = `<p>Il y a <strong>${t.a} ${ctx.objet}</strong> à ranger, et ` +
        `${ctx.chaque} peut en contenir <strong>${t.b}</strong>.</p><p>${demande}</p>`;
    const payload = makeQcm(choix, String(bonne), texte, opts(L_DIV));
    return avecParams(payload, {
        niveau: 4, dividende: t.a, diviseur: t.b, quotient: t.q, reste: t.r,
        question, reponse: bonne,
        distracteurs: choix.filter(c => c !== String(bonne)).map(c => ({ valeur: c, code: codes[c] })),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — Diviseurs communs et bouquets (niveaux 3 et 7)
// ═══════════════════════════════════════════════════════════

/** Entiers de 10 à 100, composés « riches » (4 à 10 diviseurs). */
const NOMBRES_RICHES = [
    12, 16, 18, 20, 24, 27, 28, 30, 32, 36, 40, 42, 44, 45, 48,
    50, 52, 54, 56, 63, 64, 66, 68, 70, 75, 76, 78, 88, 92,
];

const genListeDiviseursTrous = () => {
    const n = randomChoice(NOMBRES_RICHES);
    const divs = diviseursDe(n);
    const phrase = `Diviseurs de ${n} : ${divs.map(d => `{{${d}}}`).join(' ; ')}`;
    const texte = `<p>Écris <strong>tous</strong> les diviseurs de <strong>${n}</strong>, ` +
        `<strong>dans l'ordre croissant</strong>. 1 et ${n} en font partie. ` +
        `(Il y en a ${divs.length}, une case chacun.)</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_COM), mode: 'saisie' });
    return avecParams(payload, {
        niveau: 3, nombre: n, nbDiviseurs: divs.length,
        erreurVisee: 'un_et_lui_meme',
    });
};

const tirerCouple = () => {
    for (let i = 0; i < 40; i++) {
        const a = randomChoice(NOMBRES_RICHES);
        const b = randomChoice(NOMBRES_RICHES);
        if (a === b) continue;
        const communs = diviseursDe(pgcd(a, b));
        if (communs.length < 3 || communs.length > 6) continue;
        return { a, b, communs };
    }
    return { a: 18, b: 24, communs: [1, 2, 3, 6] };
};

const genCommunsQcmMulti = () => {
    const { a, b, communs } = tirerCouple();
    // Choix = diviseurs communs + diviseurs d'un seul des deux (pièges), 8 au total
    const union = [...new Set([...diviseursDe(a), ...diviseursDe(b)])];
    const pieges = shuffle(union.filter(d => !communs.includes(d))).slice(0, 8 - communs.length);
    const choix = [...communs, ...pieges].map(String);
    const texte = `<p>Coche les <strong>${MOTS_COMPTE[communs.length]}</strong> nombres qui sont ` +
        `des diviseurs communs de <strong>${a}</strong> et de <strong>${b}</strong>. ` +
        '<em>(1 compte parmi les diviseurs communs.)</em></p>';
    const payload = makeQcm(choix, communs.map(String), texte, opts(L_COM), true);
    return avecParams(payload, {
        niveau: 3, premier: a, second: b, communs,
        distracteurs: pieges.map(d => ({ valeur: String(d), code: 'un_et_lui_meme' })),
    });
};

const CTX_BOUQUETS = [
    { o1: 'roses', o2: 'tulipes', qui: 'Une fleuriste', quoi: 'bouquets', verbe: 'composer' },
    { o1: 'billes rouges', o2: 'billes bleues', qui: 'Un animateur', quoi: 'sachets', verbe: 'préparer' },
    { o1: 'madeleines', o2: 'cookies', qui: 'Un pâtissier', quoi: 'boîtes', verbe: 'garnir' },
];

const genBouquetsSaisie = () => {
    const { a, b } = tirerCouple();
    const g = pgcd(a, b);
    const ctx = randomChoice(CTX_BOUQUETS);
    const texte = `<p>${ctx.qui} dispose de <strong>${a} ${ctx.o1}</strong> et de ` +
        `<strong>${b} ${ctx.o2}</strong>, et veut ${ctx.verbe} des ${ctx.quoi} ` +
        '<strong>identiques</strong>, en utilisant tout. ' +
        `Combien de ${ctx.quoi} au <strong>maximum</strong> ?</p>`;
    const payload = makeSaisie(String(g), texte, { ...opts(L_COM), suffixe: ctx.quoi });
    return avecParams(payload, {
        niveau: 7, premier: a, second: b, reponse: g,
        erreurVisee: 'repartition_ou_reste',
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — Un multiple ou un diviseur dans un intervalle (niveau 5)
// ═══════════════════════════════════════════════════════════

/** Intervalle construit pour contenir EXACTEMENT un multiple de `a`. */
const tirerIntervalleMultiple = () => {
    for (let i = 0; i < 60; i++) {
        const a = randInt(3, 12);
        const m = a * randInt(2, Math.floor(100 / a));
        if (m < 12) continue;
        const bas = m - randInt(1, a - 1);
        const haut = m + randInt(1, a - 1);
        if (bas < 2 || haut > 100) continue;
        return { type: 'multiple', nombre: a, reponse: m, bas, haut, horsTable: m / a > 12 };
    }
    return { type: 'multiple', nombre: 7, reponse: 91, bas: 87, haut: 95, horsTable: true };
};

/** Intervalle construit pour contenir EXACTEMENT un diviseur de `n`. */
const tirerIntervalleDiviseur = () => {
    for (let i = 0; i < 60; i++) {
        const n = randomChoice(NOMBRES_RICHES);
        const divs = diviseursDe(n);
        const idx = randInt(1, divs.length - 2);
        const d = divs[idx];
        const bas = randInt(divs[idx - 1] + 1, d);
        const haut = randInt(d, divs[idx + 1] - 1);
        if (bas === haut) continue;
        return { type: 'diviseur', nombre: n, reponse: d, bas, haut, horsTable: false };
    }
    return { type: 'diviseur', nombre: 24, reponse: 6, bas: 5, haut: 7, horsTable: false };
};

const genIntervalleSaisie = () => {
    const t = randomChoice([true, false]) ? tirerIntervalleMultiple() : tirerIntervalleDiviseur();
    const texte = `<p>Un <strong>seul</strong> ${t.type} de <strong>${t.nombre}</strong> est ` +
        `compris entre <strong>${t.bas}</strong> et <strong>${t.haut}</strong> (bornes comprises). ` +
        'Écris ce nombre.</p>';
    const payload = makeSaisie(String(t.reponse), texte, opts(L_INT));
    return avecParams(payload, {
        niveau: 5, famille: t.type, nombre: t.nombre, bas: t.bas, haut: t.haut,
        reponse: t.reponse, nbSolutions: 1,
        ...(t.horsTable ? { erreurVisee: 'hors_table_donc_non_multiple' } : {}),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 8 — Simplifier une fraction (niveau 6)
// ═══════════════════════════════════════════════════════════

/** Diviseurs communs composés : un diviseur commun trop petit existe toujours. */
const PGCD_FRACTION = [4, 6, 8, 9, 10, 12];

/**
 * Fraction NON irréductible : num = d·p, den = d·q, p et q premiers entre eux,
 * p < q, den ≤ 100. Le pgcd vaut exactement d (par construction).
 */
const tirerFraction = () => {
    for (let i = 0; i < 80; i++) {
        const d = randomChoice(PGCD_FRACTION);
        const qMax = Math.floor(100 / d);
        if (qMax < 3) continue;
        const q = randInt(3, qMax);
        const p = randInt(1, q - 1);
        if (pgcd(p, q) !== 1) continue;
        const num = d * p;
        const den = d * q;
        if (den < 4) continue;
        // Un diviseur premier de q qui ne divise pas le numérateur : le piège
        // « diviseur d'un seul des deux termes ».
        const premierDeQ = [2, 3, 5, 7, 11, 13].filter(x => q % x === 0 && num % x !== 0);
        if (!premierDeQ.length) continue;
        // Un diviseur commun trop petit (simplification incomplète).
        const partiel = diviseursDe(d).filter(x => x > 1 && x < d);
        if (!partiel.length) continue;
        return {
            num, den, d, p, q,
            partiel: randomChoice(partiel),
            unSeulTerme: randomChoice(premierDeQ),
        };
    }
    return { num: 42, den: 70, d: 14, p: 3, q: 5, partiel: 2, unSeulTerme: 5 };
};

const genFractionTrous = () => {
    const t = tirerFraction();
    const phrase = `$\\dfrac{${t.num}}{${t.den}} = ${fracTrouTex(`{{${t.p}}}`, `{{${t.q}}}`)}$`;
    const texte = `<p>Simplifie ${fracTex(t.num, t.den)} <strong>le plus possible</strong> : ` +
        'cherche un diviseur commun au numérateur et au dénominateur, puis écris la ' +
        'fraction irréductible.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_FRA), mode: 'saisie' });
    return avecParams(payload, {
        niveau: 6, numerateur: t.num, denominateur: t.den, diviseurCommun: t.d,
        erreurVisee: 'un_et_lui_meme',
    });
};

const genFractionQcm = () => {
    const t = tirerFraction();
    const choix = [t.d, t.partiel, t.unSeulTerme, 1].map(String);
    const texte = `<p>Par quel nombre faut-il diviser le numérateur et le dénominateur de ` +
        `${fracTex(t.num, t.den)} pour obtenir <strong>en une seule fois</strong> la ` +
        'fraction irréductible ?</p>';
    const payload = makeQcm(choix, String(t.d), texte, opts(L_FRA));
    return avecParams(payload, {
        niveau: 6, numerateur: t.num, denominateur: t.den, reponse: t.d,
        distracteurs: [
            { valeur: String(t.partiel), code: 'simplification_incomplete' },
            { valeur: String(t.unSeulTerme), code: 'roles_inverses' },
            { valeur: '1', code: 'un_et_lui_meme' },
        ],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 9 — Deux phénomènes qui reviennent (niveau 7)
// ═══════════════════════════════════════════════════════════

const CTX_PERIODIQUE = [
    {
        unite: 'jours',
        texte: (p, q) => `<p>Au collège, le club de théâtre se réunit tous les <strong>${p} jours</strong> ` +
            `et le club de robotique tous les <strong>${q} jours</strong>. Les deux clubs se ` +
            'réunissent aujourd\'hui.</p><p>Dans combien de jours se réuniront-ils de nouveau ' +
            'le même jour ?</p>',
    },
    {
        unite: 'jours',
        texte: (p, q) => `<p>Dans une serre, les œufs d'une première espèce d'insecte éclosent tous les ` +
            `<strong>${p} jours</strong>, ceux d'une seconde espèce tous les <strong>${q} jours</strong>. ` +
            'Les deux espèces ont éclos aujourd\'hui.</p><p>Dans combien de jours écloront-elles ' +
            'de nouveau le même jour ?</p>',
    },
    {
        unite: 'secondes',
        texte: (p, q) => `<p>Deux phares s'allument, le premier toutes les <strong>${p} secondes</strong>, ` +
            `le second toutes les <strong>${q} secondes</strong>. Ils viennent de s'allumer ensemble.</p>` +
            '<p>Dans combien de secondes s\'allumeront-ils de nouveau en même temps ?</p>',
    },
];

/**
 * Deux périodes de 2 à 12, non premières entre elles (le produit est alors un
 * distracteur distinct du plus petit multiple commun), dont AUCUNE ne divise
 * l'autre (sinon la réponse est la plus grande des deux, déjà écrite dans
 * l'énoncé — défaut vu au dump), multiple commun ≤ 100, et les quatre valeurs
 * proposées deux à deux distinctes.
 */
const tirerPeriodes = () => {
    for (let i = 0; i < 80; i++) {
        const p = randInt(2, 12);
        const q = randInt(2, 12);
        if (p === q || p % q === 0 || q % p === 0) continue;
        const g = pgcd(p, q);
        if (g === 1) continue;
        const m = ppcm(p, q);
        if (m > 100 || p * q > 100) continue;
        const valeurs = [m, p + q, p * q, g];
        if (new Set(valeurs).size !== 4) continue;
        return { p, q, m, somme: p + q, produit: p * q, g };
    }
    return { p: 4, q: 6, m: 12, somme: 10, produit: 24, g: 2 };
};

const genPeriodiqueQcm = () => {
    const t = tirerPeriodes();
    const ctx = randomChoice(CTX_PERIODIQUE);
    const choix = [t.m, t.somme, t.produit, t.g].map(String);
    const payload = makeQcm(choix, String(t.m), ctx.texte(t.p, t.q), opts(L_PER));
    return avecParams(payload, {
        niveau: 7, periodes: [t.p, t.q], reponse: t.m,
        distracteurs: [
            { valeur: String(t.somme), code: 'periodes_additionnees' },
            { valeur: String(t.produit), code: 'produit_au_lieu_du_ppcm' },
            { valeur: String(t.g), code: 'diviseur_au_lieu_de_multiple' },
        ],
    });
};

const genPeriodiqueSaisie = () => {
    const t = tirerPeriodes();
    const ctx = randomChoice(CTX_PERIODIQUE);
    const payload = makeSaisie(String(t.m), ctx.texte(t.p, t.q), { ...opts(L_PER), suffixe: ctx.unite });
    return avecParams(payload, {
        niveau: 7, periodes: [t.p, t.q], reponse: t.m,
        erreurVisee: 'periodes_additionnees',
    });
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    vocabulaire_texte_trou_drag: genVocabulaireDrag,
    vocabulaire_qcm_unique:      genVocabulaireQcm,
    jugement_qcm_unique:         genJugementQcm,
    vrai_faux_tableau_vf:        genVFTableau,
    vrai_faux_qcm_multiple:      genVFQcmMulti,
    multiples_texte_trou_saisie: genMultiplesTrous,
    multiples_qcm_unique:        genMultiplesQcm,
    division_texte_trou_saisie:  genDivisionTrous,
    division_qcm_unique:         genDivisionQcm,
    division_situation_qcm:      genDivisionSituation,
    communs_texte_trou_saisie:   genListeDiviseursTrous,
    communs_qcm_multiple:        genCommunsQcmMulti,
    communs_texte:               genBouquetsSaisie,
    intervalle_texte:            genIntervalleSaisie,
    fraction_texte_trou_saisie:  genFractionTrous,
    fraction_qcm_unique:         genFractionQcm,
    periodique_qcm_unique:       genPeriodiqueQcm,
    periodique_texte:            genPeriodiqueSaisie,
};

export const CATEGORIES = [
    {
        id: 'vocabulaire', label: L_VOC,
        formatsList: [
            { key: 'vocabulaire_texte_trou_drag', label: 'Drag' },
            { key: 'vocabulaire_qcm_unique',      label: 'QCM' },
        ],
    },
    {
        id: 'jugement', label: L_JUG,
        formatsList: [{ key: 'jugement_qcm_unique', label: 'QCM' }],
    },
    {
        id: 'vrai_faux', label: L_VF,
        formatsList: [
            { key: 'vrai_faux_tableau_vf',   label: 'Vrai/Faux' },
            { key: 'vrai_faux_qcm_multiple', label: 'QCM Multi' },
        ],
    },
    {
        id: 'multiples', label: L_MUL,
        formatsList: [
            { key: 'multiples_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'multiples_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'division', label: L_DIV,
        formatsList: [
            { key: 'division_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'division_qcm_unique',        label: 'QCM' },
            { key: 'division_situation_qcm',     label: 'QCM situation' },
        ],
    },
    {
        id: 'communs', label: L_COM,
        formatsList: [
            { key: 'communs_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'communs_qcm_multiple',      label: 'QCM Multi' },
            { key: 'communs_texte',             label: 'Saisie' },
        ],
    },
    {
        id: 'intervalle', label: L_INT,
        formatsList: [{ key: 'intervalle_texte', label: 'Saisie' }],
    },
    {
        id: 'fraction', label: L_FRA,
        formatsList: [
            { key: 'fraction_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'fraction_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'periodique', label: L_PER,
        formatsList: [
            { key: 'periodique_qcm_unique', label: 'QCM' },
            { key: 'periodique_texte',      label: 'Saisie' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    pgcd, ppcm, diviseursDe, tirerFacteurs, tirerVocabulaire, tirerJugement, tirerPhrasesVF,
    tirerDivision, tirerCouple, tirerIntervalleMultiple, tirerIntervalleDiviseur,
    tirerFraction, tirerPeriodes, NOMBRES_RICHES, COMPOSES_MULTIPLE, PGCD_FRACTION,
};
